// src/pages/gestion/NouveauMarche.jsx
import React, { useState, useEffect, useRef } from "react";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate, Link } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Form,
  InputGroup,
  Button,
  Badge,
  Spinner,
  FloatingLabel,
  Alert,
  Toast,
  ToastContainer,
  ProgressBar,
  Modal
} from "react-bootstrap";
import axios from "axios";
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  ExclamationCircle,
  Search,
  InfoCircle,
  Building,
  GeoAlt,
  Shop,
  Person,
  Envelope,
  Telephone,
  Key,
  Shield,
  Plus,
  PersonPlus,
  Check,
  ChevronRight,
  ChevronLeft,
  Clipboard,
  Eye,
  EyeSlash,
  Copy
} from "react-bootstrap-icons";
import { API_BASE_URL } from "../../constants/api_constants";

const MARCHES_ENDPOINT = `${API_BASE_URL}/marches`;
const LOCALITES_ENDPOINT = `${API_BASE_URL}/localites`;
const USERS_ENDPOINT = `${API_BASE_URL}/users`;

export default function NouveauMarche() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // États pour les quartiers
  const [quartiers, setQuartiers] = useState([]);
  const [loadingQuartiers, setLoadingQuartiers] = useState(false);
  
  // États pour le formulaire du marché
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [createdMarche, setCreatedMarche] = useState(null);
  const [adminPassword, setAdminPassword] = useState(null);
  
  // État pour le modal de succès
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  
  // États pour le dropdown des quartiers
  const [quartierSearchTerm, setQuartierSearchTerm] = useState('');
  const [showQuartierDropdown, setShowQuartierDropdown] = useState(false);
  const [selectedQuartierName, setSelectedQuartierName] = useState('');
  const quartierSearchRef = useRef(null);
  
  // État du formulaire du marché
  const [formData, setFormData] = useState({
    nom_marche: '',
    id_quartier: '',
    superficie: '',
    etat: 'Ouvert',
    observations: ''
  });
  
  // État pour l'admin du marché
  const [adminData, setAdminData] = useState({
    full_name: '',
    first_name: '',
    email: '',
    telephone: '',
    fonction: 'Administrateur Marché'
  });

  const getAuthToken = () => sessionStorage.getItem("authToken");
  
  const getHeaders = () => {
    const token = getAuthToken();
    const headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  };

  // Charger les quartiers
  useEffect(() => {
    const fetchQuartiers = async () => {
      try {
        setLoadingQuartiers(true);
        const response = await axios.get(LOCALITES_ENDPOINT, { headers: getHeaders() });
        
        let allData = [];
        if (response.data && Array.isArray(response.data)) {
          allData = response.data;
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          allData = response.data.data;
        }
        
        const quartiersData = allData.filter(l => l.type_code === 1002);
        setQuartiers(quartiersData);
      } catch (error) {
        console.error("Erreur chargement quartiers:", error);
      } finally {
        setLoadingQuartiers(false);
      }
    };
    fetchQuartiers();
  }, []);

  // Filtrer les quartiers
  const getFilteredQuartiers = (searchTerm) => {
    if (!searchTerm) return quartiers;
    return quartiers.filter(q => 
      q.nom.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Sélectionner un quartier
  const handleSelectQuartier = (quartierId, quartierNom) => {
    setFormData(prev => ({ ...prev, id_quartier: quartierId }));
    setSelectedQuartierName(quartierNom);
    setQuartierSearchTerm(quartierNom);
    setShowQuartierDropdown(false);
  };

  // Gestion des clics en dehors du dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (quartierSearchRef.current && !quartierSearchRef.current.contains(event.target)) {
        setShowQuartierDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Vérifier si le step 1 est valide
  const isStep1Valid = () => {
    return formData.nom_marche.trim() && formData.id_quartier;
  };

  // Aller au step suivant
  const goToStep2 = () => {
    if (isStep1Valid()) {
      setCurrentStep(2);
    } else {
      setError('Veuillez remplir tous les champs obligatoires du marché');
    }
  };

  // Revenir au step précédent
  const goToStep1 = () => {
    setCurrentStep(1);
    setError(null);
  };

  // Copier le mot de passe
  const copyPassword = () => {
    if (adminPassword) {
      navigator.clipboard.writeText(adminPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation du marché
    if (!formData.nom_marche.trim()) {
      setError('Le nom du marché est requis');
      return;
    }
    if (!formData.id_quartier) {
      setError('Veuillez sélectionner un quartier');
      return;
    }

    // Validation de l'admin
    if (!adminData.full_name.trim()) {
      setError('Le nom complet de l\'administrateur est requis');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const payload = {
        nom_marche: formData.nom_marche.trim(),
        id_quartier: formData.id_quartier,
        superficie: formData.superficie ? parseFloat(formData.superficie) : null,
        etat: formData.etat || "Ouvert",
        observations: formData.observations || "",
        admin_full_name: adminData.full_name.trim(),
        admin_first_name: adminData.first_name.trim() || adminData.full_name.trim().split(' ')[0] || 'Admin',
        admin_email: adminData.email || `${adminData.full_name.trim().toLowerCase().replace(/\s/g, '.')}@${formData.nom_marche.trim().toLowerCase().replace(/\s/g, '')}.com`,
        admin_telephone: adminData.telephone || '',
        admin_fonction: adminData.fonction || 'Administrateur Marché'
      };
      
      console.log('📤 Payload envoyé:', payload);
      
      const response = await axios.post(MARCHES_ENDPOINT, payload, { 
        headers: getHeaders() 
      });
      
      if (response.data.code === 201 || response.data.code === 200) {
        const newMarche = response.data.data?.marche || response.data.data;
        const password = response.data.data?.admin_password;
        
        setCreatedMarche(newMarche);
        setAdminPassword(password);
        setSuccess(true);
        setShowSuccessModal(true);
      }
      
    } catch (error) {
      console.error("❌ Erreur création marché:", error);
      
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        const errorMessages = Object.values(errors).flat().join('\n');
        setError(`Erreurs de validation:\n${errorMessages}`);
      } else {
        setError(error.response?.data?.message || "Erreur lors de la création du marché");
      }
    } finally {
      setLoading(false);
    }
  };

  // Progress steps
  const StepIndicator = () => (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">
          <div className={`rounded-circle d-flex align-items-center justify-content-center ${currentStep === 1 ? 'bg-primary text-white' : 'bg-success text-white'}`} style={{ width: '32px', height: '32px' }}>
            {currentStep === 1 ? '1' : <Check size={16} />}
          </div>
          <span className={currentStep === 1 ? 'fw-bold' : 'text-muted'}>Informations du marché</span>
        </div>
        <div className="flex-grow-1 mx-3">
          <ProgressBar now={currentStep === 1 ? 50 : 100} style={{ height: '4px' }} />
        </div>
        <div className="d-flex align-items-center gap-2">
          <div className={`rounded-circle d-flex align-items-center justify-content-center ${currentStep === 2 ? 'bg-primary text-white' : currentStep > 2 ? 'bg-success text-white' : 'bg-light text-muted'}`} style={{ width: '32px', height: '32px' }}>
            {currentStep === 2 ? '2' : currentStep > 2 ? <Check size={16} /> : '2'}
          </div>
          <span className={currentStep === 2 ? 'fw-bold' : currentStep > 2 ? 'text-success' : 'text-muted'}>Administrateur</span>
        </div>
      </div>
    </div>
  );

  return (
    <React.Fragment>
      <Header />
      
      <div className="main main-app p-3 p-lg-4" style={{ background: '#f0f2f5', minHeight: '100vh' }}>
        <div className="container-fluid">
          
          {/* En-tête */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
            <div>
              <ol className="breadcrumb fs-sm mb-1">
                <li className="breadcrumb-item"><Link to="/admin/dashboard">Tableau de bord</Link></li>
                <li className="breadcrumb-item"><Link to="/gestion/Marches">Marchés</Link></li>
                <li className="breadcrumb-item active">Nouveau marché</li>
              </ol>
              <h4 className="main-title mb-0 d-flex align-items-center">
                <Building className="me-2 text-primary" size={28} />
                Nouveau marché
              </h4>
              <p className="text-muted mb-0">Créez un nouveau marché et son administrateur</p>
            </div>
            <Button 
              variant="outline-secondary" 
              onClick={() => navigate('/gestion/Marches')}
              className="mt-3 mt-md-0"
            >
              <ArrowLeft className="me-2" />
              Retour à la liste
            </Button>
          </div>

          {/* Formulaire */}
          {!success && (
            <Row>
              <Col lg={8}>
                <Card className="border-0 shadow-sm rounded-3">
                  <Card.Body className="p-4">
                    <StepIndicator />
                    
                    <Form onSubmit={currentStep === 2 ? handleSubmit : (e) => e.preventDefault()}>
                      {/* Erreur */}
                      {error && (
                        <Alert variant="danger" className="border-0">
                          <ExclamationCircle className="me-2" />
                          {error}
                        </Alert>
                      )}

                      {/* STEP 1: Informations du marché */}
                      {currentStep === 1 && (
                        <>
                          <h6 className="fw-semibold mb-3">
                            <Shop className="me-2 text-primary" size={18} />
                            Informations du marché
                          </h6>
                          
                          <Row className="g-3">
                            <Col md={12}>
                              <FloatingLabel label="Nom du marché *" className="mb-3">
                                <Form.Control
                                  type="text"
                                  placeholder="Nom du marché"
                                  value={formData.nom_marche}
                                  onChange={(e) => setFormData({ ...formData, nom_marche: e.target.value })}
                                  required
                                  isInvalid={!formData.nom_marche.trim() && error}
                                />
                              </FloatingLabel>
                            </Col>
                            
                            <Col md={12}>
                              <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">Quartier *</Form.Label>
                                <div ref={quartierSearchRef} className="position-relative">
                                  <InputGroup>
                                    <InputGroup.Text className="bg-light border-end-0">
                                      <Search size={16} className="text-primary" />
                                    </InputGroup.Text>
                                    <Form.Control
                                      type="text"
                                      placeholder="Rechercher un quartier..."
                                      value={quartierSearchTerm}
                                      onChange={(e) => {
                                        setQuartierSearchTerm(e.target.value);
                                        setShowQuartierDropdown(true);
                                        if (!e.target.value) {
                                          setFormData(prev => ({ ...prev, id_quartier: "" }));
                                          setSelectedQuartierName('');
                                        }
                                      }}
                                      onFocus={() => setShowQuartierDropdown(true)}
                                      className="border-start-0 bg-light"
                                      isInvalid={!formData.id_quartier && error}
                                    />
                                    {formData.id_quartier && (
                                      <Button 
                                        variant="outline-secondary" 
                                        className="border-start-0"
                                        onClick={() => {
                                          setQuartierSearchTerm('');
                                          setSelectedQuartierName('');
                                          setFormData(prev => ({ ...prev, id_quartier: "" }));
                                          setShowQuartierDropdown(false);
                                        }}
                                      >
                                        <XCircle size={16} />
                                      </Button>
                                    )}
                                  </InputGroup>
                                  
                                  {showQuartierDropdown && (
                                    <div 
                                      className="position-absolute w-100 mt-1 bg-white rounded-3 shadow-lg border"
                                      style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}
                                    >
                                      {loadingQuartiers ? (
                                        <div className="p-3 text-center">
                                          <Spinner animation="border" size="sm" />
                                          <span className="ms-2">Chargement...</span>
                                        </div>
                                      ) : getFilteredQuartiers(quartierSearchTerm).length > 0 ? (
                                        getFilteredQuartiers(quartierSearchTerm).map(quartier => (
                                          <div
                                            key={quartier.localite_id}
                                            className="p-2 border-bottom hover-bg-light cursor-pointer d-flex justify-content-between align-items-center"
                                            onClick={() => handleSelectQuartier(quartier.localite_id, quartier.nom)}
                                            style={{ cursor: 'pointer' }}
                                          >
                                            <span className="fw-medium">
                                              <GeoAlt className="me-2 text-primary" size={14} />
                                              {quartier.nom}
                                              {formData.id_quartier === quartier.localite_id && (
                                                <CheckCircle className="ms-2 text-success" size={14} />
                                              )}
                                            </span>
                                            <Badge bg="secondary">Quartier</Badge>
                                          </div>
                                        ))
                                      ) : (
                                        <div className="p-3 text-center text-muted">
                                          <InfoCircle className="me-2" />
                                          Aucun quartier trouvé
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                {selectedQuartierName && (
                                  <div className="mt-2">
                                    <Badge bg="success" className="px-3 py-2 rounded-pill">
                                      <CheckCircle className="me-1" size={12} />
                                      Quartier sélectionné : {selectedQuartierName}
                                    </Badge>
                                  </div>
                                )}
                              </Form.Group>
                            </Col>
                            
                            <Col md={6}>
                              <FloatingLabel label="Superficie (m²)" className="mb-3">
                                <Form.Control
                                  type="number"
                                  step="0.01"
                                  placeholder="Superficie en m²"
                                  value={formData.superficie}
                                  onChange={(e) => setFormData({ ...formData, superficie: e.target.value })}
                                />
                              </FloatingLabel>
                            </Col>
                            
                            <Col md={6}>
                              <FloatingLabel label="État" className="mb-3">
                                <Form.Select
                                  value={formData.etat}
                                  onChange={(e) => setFormData({ ...formData, etat: e.target.value })}
                                >
                                  <option value="Ouvert">Ouvert</option>
                                  <option value="Fermé">Fermé</option>
                                </Form.Select>
                              </FloatingLabel>
                            </Col>
                            
                            <Col md={12}>
                              <Form.Group>
                                <Form.Label className="fw-semibold">Observations</Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={2}
                                  placeholder="Observations (optionnel)"
                                  value={formData.observations}
                                  onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          <div className="d-flex justify-content-end mt-4">
                            <Button 
                              variant="primary" 
                              onClick={goToStep2}
                              disabled={!isStep1Valid()}
                              className="px-4"
                            >
                              Suivant <ChevronRight className="ms-2" />
                            </Button>
                          </div>
                        </>
                      )}

                      {/* STEP 2: Administrateur */}
                      {currentStep === 2 && (
                        <>
                          <div className="mb-3">
                            <h6 className="fw-semibold">
                              <PersonPlus className="me-2 text-primary" size={18} />
                              Administrateur du marché <span className="text-danger">*</span>
                            </h6>
                            <Form.Text className="text-muted">
                              L'administrateur aura accès au tableau de bord du marché (rôle 1005)
                            </Form.Text>
                          </div>

                          <div className="bg-light p-3 rounded-3 mb-3">
                            <Row className="g-3">
                              <Col md={6}>
                                <FloatingLabel label="Nom complet *" className="mb-3">
                                  <Form.Control
                                    type="text"
                                    placeholder="Nom complet"
                                    value={adminData.full_name}
                                    onChange={(e) => setAdminData({ ...adminData, full_name: e.target.value })}
                                    required
                                    isInvalid={!adminData.full_name.trim() && error}
                                  />
                                </FloatingLabel>
                              </Col>
                              <Col md={6}>
                                <FloatingLabel label="Prénom" className="mb-3">
                                  <Form.Control
                                    type="text"
                                    placeholder="Prénom"
                                    value={adminData.first_name}
                                    onChange={(e) => setAdminData({ ...adminData, first_name: e.target.value })}
                                  />
                                </FloatingLabel>
                              </Col>
                              <Col md={6}>
                                <FloatingLabel label="Email" className="mb-3">
                                  <Form.Control
                                    type="email"
                                    placeholder="Email"
                                    value={adminData.email}
                                    onChange={(e) => setAdminData({ ...adminData, email: e.target.value })}
                                  />
                                  <Form.Text className="text-muted">
                                    Si vide, un email sera généré automatiquement
                                  </Form.Text>
                                </FloatingLabel>
                              </Col>
                              <Col md={6}>
                                <FloatingLabel label="Téléphone" className="mb-3">
                                  <Form.Control
                                    type="text"
                                    placeholder="Téléphone"
                                    value={adminData.telephone}
                                    onChange={(e) => setAdminData({ ...adminData, telephone: e.target.value })}
                                  />
                                </FloatingLabel>
                              </Col>
                              <Col md={12}>
                                <FloatingLabel label="Fonction" className="mb-3">
                                  <Form.Control
                                    type="text"
                                    placeholder="Fonction"
                                    value={adminData.fonction}
                                    onChange={(e) => setAdminData({ ...adminData, fonction: e.target.value })}
                                  />
                                </FloatingLabel>
                              </Col>
                            </Row>
                            <div className="mt-2 text-muted small">
                              <Shield className="me-1" size={14} />
                              L'administrateur aura le rôle <strong>Admin Marché</strong> (1005)
                              {formData.id_quartier && (
                                <span className="ms-2">
                                  • Marché: <strong>{formData.nom_marche || 'à définir'}</strong>
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="d-flex gap-2 mt-4">
                            <Button 
                              variant="outline-secondary" 
                              onClick={goToStep1}
                              disabled={loading}
                            >
                              <ChevronLeft className="me-2" /> Précédent
                            </Button>
                            <Button 
                              variant="secondary" 
                              onClick={() => navigate('/gestion/Marches')}
                              disabled={loading}
                            >
                              Annuler
                            </Button>
                            <Button 
                              variant="primary" 
                              type="submit" 
                              disabled={loading || !adminData.full_name.trim()}
                              className="px-4 ms-auto"
                            >
                              {loading ? (
                                <><Spinner size="sm" className="me-2" />Création...</>
                              ) : (
                                <><Check className="me-2" />Créer le marché</>
                              )}
                            </Button>
                          </div>
                        </>
                      )}
                    </Form>
                  </Card.Body>
                </Card>
              </Col>
              
              {/* Sidebar d'information - STICKY */}
              <Col lg={4}>
                <div style={{ position: 'sticky', top: '20px' }}>
                  <Card className="border-0 shadow-sm rounded-3">
                    <Card.Header className="bg-primary text-white border-bottom-0">
                      <h6 className="mb-0 fw-semibold">
                        <InfoCircle className="me-2" />
                        Informations
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      {currentStep === 1 ? (
                        <>
                          <div className="mb-3">
                            <h6 className="fw-semibold"> Étape 1 : Marché</h6>
                            <ul className="text-muted small ps-3">
                              <li>Le nom du marché est obligatoire</li>
                              <li>Le marché doit être associé à un quartier</li>
                              <li>Le code du marché est généré automatiquement</li>
                              <li>Un ID unique est attribué à chaque marché</li>
                            </ul>
                          </div>
                          <hr />
                          <div>
                            <h6 className="fw-semibold"> Prochaine étape</h6>
                            <p className="text-muted small">
                              Après avoir renseigné les informations du marché, vous pourrez créer son administrateur.
                            </p>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="mb-3">
                            <h6 className="fw-semibold"> Rôle de l'administrateur</h6>
                            <p className="text-muted small">
                              L'administrateur du marché aura les droits de gestion sur :
                            </p>
                            <ul className="text-muted small ps-3">
                              <li>Gestion des contribuables du marché</li>
                              <li>Suivi des collectes</li>
                              <li>Performance des agents</li>
                              <li>Objectifs du marché</li>
                              <li>Rapports et analyses</li>
                            </ul>
                          </div>
                          <hr />
                          <div>
                            <h6 className="fw-semibold"> À savoir</h6>
                            <ul className="text-muted small ps-3">
                              <li>Un administrateur est obligatoire pour chaque marché</li>
                              <li>Un mot de passe temporaire sera généré</li>
                              <li>L'admin aura le rôle 1005</li>
                            </ul>
                          </div>
                        </>
                      )}
                      <hr />
                      <div className="bg-warning bg-opacity-10 p-3 rounded-3">
                        <ExclamationCircle className="text-warning me-2" size={18} />
                        <span className="small fw-medium">
                          {currentStep === 1 
                            ? 'Tous les champs marqués * sont obligatoires'
                            : 'L\'administrateur est obligatoire pour chaque marché'
                          }
                        </span>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              </Col>
            </Row>
          )}

          <Footer />
        </div>
      </div>

      {/* ✅ MODAL DE SUCCÈS - NE SE FERME PAS AUTOMATIQUEMENT */}
      <Modal 
        show={showSuccessModal} 
        onHide={() => {}} // ✅ Ne se ferme pas
        centered 
        size="lg"
        backdrop="static" // ✅ Empêche la fermeture en cliquant à l'extérieur
        keyboard={false} // ✅ Empêche la fermeture avec la touche Echap
      >
        <Modal.Header className="border-0 text-white" style={{ background: 'linear-gradient(135deg, #28a745, #1e7e34)' }}>
          <Modal.Title className="d-flex align-items-center">
            <CheckCircle size={28} className="me-2" />
            Marché créé avec succès !
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="text-center mb-4">
            <div className="rounded-circle bg-success bg-opacity-10 d-flex align-items-center justify-content-center mx-auto" style={{ width: '80px', height: '80px' }}>
              <CheckCircle size={48} className="text-success" />
            </div>
            <h4 className="mt-3 fw-bold">{formData.nom_marche}</h4>
            <p className="text-muted">Le marché a été enregistré avec succès</p>
          </div>

          <Row className="g-3">
            <Col md={6}>
              <div className="p-3 bg-light rounded-3">
                <small className="text-muted d-block">ID du marché</small>
                <code className="fw-bold fs-5">{createdMarche?.id_marche || '-'}</code>
              </div>
            </Col>
            <Col md={6}>
              <div className="p-3 bg-light rounded-3">
                <small className="text-muted d-block">Code du marché</small>
                <code className="fw-bold fs-5">{createdMarche?.code_marche || '-'}</code>
              </div>
            </Col>
            <Col md={12}>
              <div className="p-3 bg-light rounded-3">
                <small className="text-muted d-block">Administrateur</small>
                <div className="d-flex align-items-center">
                  <Person size={18} className="text-primary me-2" />
                  <span className="fw-bold">{adminData.full_name}</span>
                  <Badge bg="info" className="ms-2">Admin Marché (1005)</Badge>
                </div>
              </div>
            </Col>
            <Col md={12}>
              <div className="p-3 bg-warning bg-opacity-10 rounded-3 border border-warning">
                <div className="d-flex align-items-start">
                  <Key size={20} className="text-warning me-2 mt-1" />
                  <div className="flex-grow-1">
                    <small className="text-muted d-block">Mot de passe temporaire</small>
                    <div className="d-flex align-items-center gap-2">
                      <code className="fw-bold fs-4 text-success" style={{ letterSpacing: '2px' }}>
                        {showPassword ? adminPassword : '••••••••'}
                      </code>
                      <Button 
                        variant="outline-secondary" 
                        size="sm"
                        onClick={() => setShowPassword(!showPassword)}
                        title={showPassword ? 'Masquer' : 'Afficher'}
                      >
                        {showPassword ? <EyeSlash size={14} /> : <Eye size={14} />}
                      </Button>
                      <Button 
                        variant="outline-success" 
                        size="sm"
                        onClick={copyPassword}
                        title="Copier"
                      >
                        {copied ? <Check size={14} className="text-success" /> : <Copy size={14} />}
                      </Button>
                    </div>
                    <small className="text-muted d-block mt-1">
                      Veuillez communiquer ce mot de passe à l'administrateur.
                      {copied && <span className="text-success ms-2">✓ Copié !</span>}
                    </small>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <div className="d-flex gap-2 w-100">
            <Button 
              variant="outline-success" 
              className="flex-grow-1"
              onClick={() => {
                setShowSuccessModal(false);
                setSuccess(false);
                setCreatedMarche(null);
                setAdminPassword(null);
                setCurrentStep(1);
                setFormData({ nom_marche: '', id_quartier: '', superficie: '', etat: 'Ouvert', observations: '' });
                setSelectedQuartierName('');
                setQuartierSearchTerm('');
                setAdminData({ full_name: '', first_name: '', email: '', telephone: '', fonction: 'Administrateur Marché' });
              }}
            >
              <Plus className="me-2" />
              Ajouter un autre marché
            </Button>
            <Button 
              variant="success" 
              className="flex-grow-1"
              onClick={() => navigate('/gestion/Marches')}
            >
              <Building className="me-2" />
              Voir la liste des marchés
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <style>{`
        .cursor-pointer {
          cursor: pointer;
        }
        .hover-bg-light:hover {
          background-color: #f8f9fa !important;
        }
        .main-title {
          font-weight: 700;
        }
        .card {
          border-radius: 12px !important;
        }
        .shadow-sm {
          box-shadow: 0 2px 8px rgba(0,0,0,0.06) !important;
        }
        .bg-light {
          background-color: #f8f9fa !important;
        }
        .modal-content {
          border-radius: 16px !important;
          border: none !important;
        }
      `}</style>
    </React.Fragment>
  );
}